import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  fetchPendingApprovals,
  processApproval,
} from '../apis/reconAuditApi';

const thStyle = {
  textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: 700,
  color: '#0039A6', textTransform: 'uppercase', letterSpacing: '0.5px',
  borderBottom: '2px solid #0098DB', backgroundColor: '#f8f9fa', whiteSpace: 'nowrap',
};
const tdStyle = {
  padding: '10px 12px', fontSize: '13px', color: '#333',
  borderBottom: '1px solid #eee', verticalAlign: 'top',
};

function formatRequestedDate(value) {
  if (value == null || value === '') return '';
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : String(value);
}

export function ApprovalsQueue() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingIds, setProcessingIds] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPendingApprovals()
      .then((list) => { if (!cancelled) setApprovals(list); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load pending approvals'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const runAction = useCallback(async (reconId, action) => {
    if (processingIds[reconId]) return;
    setProcessingIds((prev) => ({ ...prev, [reconId]: true }));
    setError(null);
    try {
      await processApproval(reconId, action);
      setApprovals((prev) => prev.filter((r) => r.reconId !== reconId));
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} (recon ${reconId})`);
    } finally {
      setProcessingIds((prev) => { const next = { ...prev }; delete next[reconId]; return next; });
    }
  }, [processingIds]);

  const handleApprove = useCallback((id) => runAction(id, 'approve'), [runAction]);
  const handleReject = useCallback((id) => runAction(id, 'reject'), [runAction]);

  return (
    <div>
      {error && (
        <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', color: '#991b1b', fontSize: '13px', marginBottom: '15px' }}>
          {error}
        </div>
      )}

      <article className="card">
        <header>
          <i className="fa fa-check-square-o"></i>
          <label>Pending Approvals ({approvals.length})</label>
        </header>
        <div className="body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999', fontSize: '14px' }}>
              <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>Loading…
            </div>
          ) : approvals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999', fontSize: '14px' }}>
              No pending approvals.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>IM Order #</th>
                    <th style={thStyle}>Requested By</th>
                    <th style={thStyle}>Comment</th>
                    <th style={thStyle}>Requested Date</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {approvals.map((approval) => (
                    <tr
                      key={approval.reconId}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f7ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                    >
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{approval.orderNum ?? ''}</td>
                      <td style={tdStyle}>{approval.requestedBy ?? ''}</td>
                      <td style={{ ...tdStyle, maxWidth: '300px' }}>{approval.comments ?? ''}</td>
                      <td style={{ ...tdStyle, color: '#666' }}>{formatRequestedDate(approval.requestedDate)}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            type="button"
                            disabled={!!processingIds[approval.reconId]}
                            onClick={() => handleApprove(approval.reconId)}
                            className="btn-blue"
                            style={{ padding: '4px 12px', height: '30px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', opacity: processingIds[approval.reconId] ? 0.6 : 1 }}
                          >
                            {processingIds[approval.reconId]
                              ? <><i className="fa fa-spinner fa-spin"></i> Processing…</>
                              : <><i className="fa fa-check"></i> Approve</>}
                          </button>
                          <button
                            type="button"
                            disabled={!!processingIds[approval.reconId]}
                            onClick={() => handleReject(approval.reconId)}
                            className="btn-red"
                            style={{ padding: '4px 12px', height: '30px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', opacity: processingIds[approval.reconId] ? 0.6 : 1 }}
                          >
                            {processingIds[approval.reconId]
                              ? <><i className="fa fa-spinner fa-spin"></i> Processing…</>
                              : <><i className="fa fa-times"></i> Reject</>}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </article>
    </div>
  );
}
